// Shared constants and pre-written parser presets for weighbridge serial parsing.
// Extracted from the original WeighbridgeSetupPage so the Setup Groups page and its
// sub-components can reuse them.

export const PARSER_PRESETS = [
    {
        value: 0,
        label: 'Custom (write your own)',
        description: 'Write a custom parser for an unknown weighbridge format',
        parser: `// Write your custom parser here.
// 'data' is the raw serial line (string).
// Return a number (weight in tons) or null if not a valid reading.
// Raw data is usually in kg — divide by 1000 to convert to tons.

function parse(data) {
  // TODO: modify this to match your weighbridge format
  return null;
}`,
    },
    {
        value: 1,
        label: 'Orion',
        description: 'Line format: GS, 12345, kg',
        sampleOutput: 'GS, 12345, kg',
        parser: `// Orion weighbridge parser
// Format: "GS, {weight}, kg"
// Example: "GS, 12345, kg" → 12.345 ton

function parse(data) {
  var parts = data.split(",");
  if (parts.length >= 2 && data.indexOf("GS") === 0) {
    var weightKg = parseFloat(parts[1].trim());
    if (!isNaN(weightKg)) {
      return weightKg / 1000;
    }
  }
  return null;
}`,
    },
    {
        value: 2,
        label: 'Essae',
        description: 'Line format: 0:12345 (alternates with 00000 heartbeat)',
        sampleOutput: '0:12345',
        parser: `// Essae weighbridge parser
// Format: "0:{weight}" (alternates with "00000" heartbeat)
// Example: "0:12345" → 12.345 ton, "00000" → skip (heartbeat)

function parse(data) {
  var trimmed = data.trim();
  // Skip heartbeat lines
  if (trimmed === "00000") return null;
  // Match "0:{weight}" pattern
  var match = trimmed.match(/^0:(\\d+)$/);
  if (match) {
    var weightKg = parseFloat(match[1]);
    if (!isNaN(weightKg) && weightKg > 0) {
      return weightKg / 1000;
    }
  }
  return null;
}`,
    },
    {
        value: 3,
        label: 'Line with Sequence',
        description: 'Line format: a12345',
        sampleOutput: 'a12345',
        parser: `// Line with Sequence weighbridge parser
// Format: "a{weight}"
// Example: "a12345" → 12.345 ton

function parse(data) {
  var trimmed = data.trim();
  var match = trimmed.match(/^a(\\d+)$/);
  if (match) {
    var weightKg = parseFloat(match[1]);
    if (!isNaN(weightKg) && weightKg > 0) {
      return weightKg / 1000;
    }
  }
  return null;
}`,
    },
    {
        value: 4,
        label: 'Straight with Delimiter',
        description: 'Continuous stream: 12345= (delimiter-separated, no newlines)',
        sampleOutput: '12345=',
        parser: `// Straight with Delimiter weighbridge parser
// Format: "{weight}{delimiter}" - continuous stream, no newlines
// Example: "12345=12345=12345=" → 12.345 ton
// Change the DELIMITER below to match your weighbridge

var DELIMITER = "=";

function parse(data) {
  var parts = data.split(DELIMITER).filter(function(p) { return p.trim().length > 0; });
  for (var i = parts.length - 1; i >= 0; i--) {
    var weightKg = parseFloat(parts[i].trim());
    if (!isNaN(weightKg) && weightKg > 0) {
      return weightKg / 1000;
    }
  }
  return null;
}`,
    },
    {
        value: 5,
        label: 'Reverse with Delimiter',
        description: 'Continuous stream: 54321= (digits reversed, delimiter-separated)',
        sampleOutput: '54321=',
        parser: `// Reverse with Delimiter weighbridge parser
// Format: "{reversed_weight}{delimiter}" - digits are reversed
// Example: "54321=54321=54321=" → last reading "54321" → reverse → "12345" → 12.345 ton
// Change the DELIMITER below to match your weighbridge

var DELIMITER = "=";

function parse(data) {
  var parts = data.split(DELIMITER).filter(function(p) { return p.trim().length > 0; });
  for (var i = parts.length - 1; i >= 0; i--) {
    var reversed = parts[i].trim().split("").reverse().join("");
    var weightKg = parseFloat(reversed);
    if (!isNaN(weightKg) && weightKg > 0) {
      return weightKg / 1000;
    }
  }
  return null;
}`,
    },
    {
        value: 6,
        label: 'Custom Type 1 (Reverse Decimal)',
        description: 'Continuous stream: 0123.4500= (reversed, divided by 100)',
        sampleOutput: '0123.4500=',
        parser: `// Custom Type 1 weighbridge parser
// Format: "{reversed_weight/100 as 8-char decimal}{delimiter}"
// Example: "0123.4500=0123.4500=" → last reading "0123.4500" → ×100 → 12345 → reverse → 54321 kg
// Change the DELIMITER below to match your weighbridge

var DELIMITER = "=";

function parse(data) {
  var parts = data.split(DELIMITER).filter(function(p) { return p.trim().length > 0; });
  for (var i = parts.length - 1; i >= 0; i--) {
    var rawValue = parseFloat(parts[i].trim());
    if (!isNaN(rawValue)) {
      var asInt = Math.round(rawValue * 100);
      var reversed = asInt.toString().split("").reverse().join("");
      var weight = parseFloat(reversed);
      if (!isNaN(weight) && weight > 0) {
        return weight / 1000;
      }
    }
  }
  return null;
}`,
    },
    {
        value: 7,
        label: 'Custom Type 2',
        description: 'Line format: &&00 12345  00',
        sampleOutput: ' &&00 12345  00',
        parser: `// Custom Type 2 weighbridge parser
// Format: " &&00 {weight}  00"
// Example: " &&00 12345  00" → 12.345 ton

function parse(data) {
  var match = data.match(/&&00\\s+(\\d+)\\s+00/);
  if (match) {
    var weightKg = parseFloat(match[1]);
    if (!isNaN(weightKg) && weightKg > 0) {
      return weightKg / 1000;
    }
  }
  return null;
}`,
    },
    {
        value: 8,
        label: 'Custom Type 4 (Weight / 10)',
        description: 'Line format: a1234 (actual weight = value x 10)',
        sampleOutput: 'a1234',
        parser: `// Custom Type 4 weighbridge parser
// Format: "a{weight/10}" - transmitted value is actual weight divided by 10
// Example: "a1234" → 1234 × 10 = 12340 kg → 12.34 ton

function parse(data) {
  var trimmed = data.trim();
  var match = trimmed.match(/^a(\\d+)$/);
  if (match) {
    var rawValue = parseFloat(match[1]);
    if (!isNaN(rawValue) && rawValue > 0) {
      return (rawValue * 10) / 1000;
    }
  }
  return null;
}`,
    },
    {
        value: 9,
        label: 'Custom Type 5',
        description: 'Line format: [0012345]#001+04.012',
        sampleOutput: '[0012345]#001+04.012',
        parser: `// Custom Type 5 weighbridge parser
// Format: "[{weight 7-digit zero-padded}]#001+04.012"
// Example: "[0012345]#001+04.012" → 12.345 ton

function parse(data) {
  var match = data.match(/\\[(\\d+)\\]/);
  if (match) {
    var weightKg = parseFloat(match[1]);
    if (!isNaN(weightKg) && weightKg > 0) {
      return weightKg / 1000;
    }
  }
  return null;
}`,
    },
]

export const BAUD_RATES = [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200]
export const DATA_BITS_OPTIONS = [5, 6, 7, 8]
export const STOP_BITS_OPTIONS = [1, 2]
export const PARITY_OPTIONS = ['NONE', 'EVEN', 'ODD', 'MARK', 'SPACE']
export const FLOW_CONTROL_OPTIONS = ['NONE', 'RTSCTS', 'XONXOFF']
export const MAX_SERIAL_LINES = 200

// Factory defaults for a brand-new group's camera + weighbridge sub-objects.
export const defaultCamera = () => ({
    protocol: 'rtsp',
    host: '',
    port: '',
    username: '',
    password: '',
    path: '',
})

export const defaultWeighbridge = () => ({
    brandName: '',
    weighbridgeType: 1,
    serialPort: '',
    baudRate: 115200,
    dataBits: 8,
    stopBits: 1,
    parity: 'NONE',
    flowControl: 'NONE',
    parserFunction: PARSER_PRESETS[1].parser,
    readTimeoutMs: 5000,
    bufferFlushSize: 50,
    stabilizationToleranceKg: 25,
    enabled: true,
})
